// The map layers the area picker offers, and what each one is used for. Which layers a country has
// comes from /maps/list (the table names it returns); the registry decides what each is called and
// which products read it. A layer the list returns is always shown, even when no product can use it.
// May import countries.ts and products.ts only. No React, router or Firebase imports.
import type { CountryCode, MapType } from './countries';
import { PRODUCT_LABELS, geoCapability, geoTypeLabel } from './products';
import { catalogueGeoTypeOrder } from './catalogue';

/** The prefix of the layer names the catalogue maps are listed under (services/catalogueMapsApi.ts). */
const CATALOGUE_PREFIX = 'catalogue:';

/**
 * Picker geo type for a /maps/list table_name; null for anything we don't recognise.
 * Order matters: '250' before '1000', and level 4 before level 2.
 */
export function mapLayerGeoType(tableName: string): MapType | null {
  // A catalogue layer is named by its own geo type.
  if (tableName.startsWith(CATALOGUE_PREFIX)) return tableName.slice(CATALOGUE_PREFIX.length) as MapType;
  if (tableName.includes('250')) return 'grid_250m';
  if (tableName.includes('1000')) return 'grid_1000m';
  if (tableName.includes('admin_level_4')) return 'admin_level_4';
  if (tableName.includes('admin_level_2')) return 'admin_level_2';
  return null;
}

export interface LayerUse {
  product: 'activities' | 'odm';
  label: string;
  blockedReason: string | null;
}

/** Products whose capability lists this geo type for the country. */
export function layerUses(c: CountryCode, geoType: string): LayerUse[] {
  const out: LayerUse[] = [];
  for (const p of ['activities', 'odm'] as const) {
    const geo = geoCapability(c, p, geoType);
    if (geo) out.push({ product: p, label: PRODUCT_LABELS[p], blockedReason: geo.blocked?.reason ?? null });
  }
  return out;
}

/** "A", "A and B" or "A, B and C". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The plain lines under a layer. The first line always starts "Works with: ". */
export function layerUseLines(c: CountryCode, geoType: string): string[] {
  const uses = layerUses(c, geoType);
  const usable = uses.filter((u) => !u.blockedReason);
  const lines = [`Works with: ${usable.length ? usable.map((u) => u.label).join(', ') : 'not used by any product yet'}`];
  const reasons = new Map<string, string[]>();
  for (const u of uses) {
    if (!u.blockedReason) continue;
    reasons.set(u.blockedReason, [...(reasons.get(u.blockedReason) ?? []), u.label]);
  }
  reasons.forEach((names, reason) => lines.push(`Not yet for ${joinNames(names)}: ${reason}`));
  return lines;
}

const PICKER_ORDER: readonly MapType[] = ['admin_level_2', 'admin_level_4', 'grid_1000m', 'grid_250m'];

export interface PickerLayer {
  geoType: MapType;
  label: string;
  description: string;
  uses: string[];
}

/**
 * Recognised rows, one per geo type (the first row wins), in a fixed order, labelled from the registry. The database description is kept.
 */
export function pickerLayers(
  c: CountryCode,
  rows: readonly { table_name?: string; description?: string }[],
): PickerLayer[] {
  const first = new Map<MapType, string>();
  for (const row of rows) {
    const geoType = mapLayerGeoType(row.table_name ?? '');
    if (!geoType || first.has(geoType)) continue;
    first.set(geoType, row.description ?? '');
  }
  // The catalogue's layers go by depth and then size; the old maps keep their fixed order.
  const catalogue = rows.some((r) => r.table_name?.startsWith(CATALOGUE_PREFIX));
  const order = catalogue
    ? Array.from(first.keys()).sort((a, b) => catalogueGeoTypeOrder(a) - catalogueGeoTypeOrder(b))
    : PICKER_ORDER;
  const out: PickerLayer[] = [];
  for (const geoType of order) {
    const description = first.get(geoType);
    if (description === undefined) continue;
    out.push({ geoType, label: geoTypeLabel(geoType, c), description, uses: layerUseLines(c, geoType) });
  }
  return out;
}
